import { type ChangeEvent, Dispatch, SetStateAction } from 'react';

import type { CountryCode } from 'libphonenumber-js';
import { type Country } from 'react-easyrocket';
import { getFormattedNumber } from '../../utils/getFormattedNumber';

interface UsePhoneInputHandlersInput {
  phoneNumber: string | undefined;
  selectedCountry: Country | undefined;
  setPhoneNumber: Dispatch<SetStateAction<string | undefined>>;
  currentCountryCode: CountryCode;
}

interface UsePhoneInputHandlersOutput {
  handleInput: (event: ChangeEvent<HTMLInputElement>) => void;
  handleBeforeInput: (event: ChangeEvent<HTMLInputElement>) => void;
}

export const usePhoneInputHandlers = ({
  phoneNumber,
  selectedCountry,
  setPhoneNumber,
  currentCountryCode
}: UsePhoneInputHandlersInput): UsePhoneInputHandlersOutput => {
  const handleInput = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;

    const countryCodePrefix = selectedCountry?.phoneCodes[0];

    if (countryCodePrefix && !value.startsWith(countryCodePrefix)) {
      const newValue = countryCodePrefix + value.slice(countryCodePrefix?.length + 1);
      setPhoneNumber(newValue);
      return;
    }

    const formattedNumber = getFormattedNumber(currentCountryCode, value);

    if (countryCodePrefix && !formattedNumber.startsWith(countryCodePrefix)) {
      setPhoneNumber(countryCodePrefix + formattedNumber.slice(countryCodePrefix?.length));
    } else {
      setPhoneNumber(formattedNumber);
    }
  };

  const handleBeforeInput = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const prefix = selectedCountry?.phoneCodes[0] || '';
    const selectionStart = input.selectionStart ?? 0;

    if (selectionStart <= prefix.length && event && 'data' in event) {
      event.preventDefault();

      const newValue = (phoneNumber || '') + event.data;
      const formatted = getFormattedNumber(currentCountryCode, newValue);
      setPhoneNumber(formatted);

      requestAnimationFrame(() => {
        input.setSelectionRange(formatted.length, formatted.length);
      });
    }
  };

  return { handleInput, handleBeforeInput };
};
